<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells>
        <h1 class="title">Payment ID: {{ $route.params.id }}</h1>
        <div class="manager-title">
          <div class="manager-title__photo">
            <img :src="getUserAvatar(payment_data.created_by.crop.crop_url)" alt="Avatar">
          </div>
          <span class="manager-title__name">{{ payment_data.created_by.full_name }}</span>
        </div>
        <span class="title__update">Date: {{ dateTimeFormat(payment_data.created_at) }}</span>
        <div
          class="status-tag"
          :style="{ 'backgroundColor': parseInt(payment_data.stats.balance) === 0 ? '#77b017' : parseInt(payment_data.stats.balance) > 0 ? '#439ad1' :'#e85c20' }"
        >
          Balance: {{ formatAmount(payment_data.stats.balance) }} $
        </div>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Lead:</span>
              <router-link
                v-if="group_data.lead"
                :to="{ name: 'leads-detail-lead', params: { id: group_data.lead.id } }"
                class="info-cards__link"
              >
                {{ group_data.lead.name }}
              </router-link>
              <span v-else>-</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Story:</span>
              <a
                :href="`https://story.altezza.travel/clients/${group_data.lead?.final_itinerary?.itinerary_id}`"
                target="_blank"
                class="table-default__activity table-default__activity--itinerary"
                :class="{ 'table-default__activity--active': group_data.lead?.final_itinerary?.itinerary_id }"
              />
              <a
                :href="`https://story.altezza.travel/itinerary/${group_data.lead?.final_itinerary?.hash}`"
                target="_blank"
                class="table-default__activity table-default__activity--clients"
                :class="{ 'table-default__activity--active': group_data.lead?.final_itinerary?.hash }"
              />
            </div>
            <div class="info-cards__row-item">
              <span>External:</span>
              <a
                :href="group_data.lead?.asana"
                target="_blank"
                class="table-default__activity table-default__activity--asana"
                :class="{'table-default__activity--active': group_data.lead?.asana}"
              />
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Tour operator:</span>
              <span>{{ group_data.lead?.tour_operator ? group_data.lead.tour_operator.name : '-' }}</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Gear:</span><span>{{ formatAmount(payment_data.stats.total_sum_gears) }} $</span>
            </div>
            <div class="info-cards__row-item">
              <span>Invoices:</span><span>{{ formatAmount(payment_data.stats.total_sum_invoices) }} $</span>
            </div>

          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Services:</span><span>{{ formatAmount(payment_data.stats.total_sum_add_services) }} $</span>
            </div>
            <div class="info-cards__row-item">
              <span>Debt:</span><span>{{ formatAmount(payment_data.stats.total_debt) }} $</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Cash:</span><span>{{ formatAmount(payment_data.stats.total_sum_cash) }} $</span>
            </div>
            <div class="info-cards__row-item">
              <span>Change:</span><span>{{ formatAmount(payment_data.stats.total_sum_change) }} $</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>CC:</span><span>{{ formatAmount(payment_data.stats.total_sum_cc) }} $</span>
            </div>
            <div class="info-cards__row-item">
              <span>Payments:</span><span>{{ formatAmount(payment_data.stats.total_payments) }} $</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Other:</span><span>{{ formatAmount(payment_data.stats.total_sum_other_payments) }} $</span>
            </div>
          </div>
        </div>
      </div>
      <app-colored-notes
        v-if="payment_data.additional_notes"
        color="#35a4ea"
        :text="payment_data.additional_notes"
        class="colored-notes--indent-30"
      />
      <app-grid class="grid--align-center">
        <template #item-1>
          <app-cells>
            <h2 class="title">Cash</h2>
            <app-button size="small" @click.native="onShowModal(null, 'Cash', 'editModal')">Add cash</app-button>
            <div v-if="payment_data.change || table_cash.items.length" class="attention-info attention-info--no-indent">
              <p>
                <template v-if="!payment_data.change && table_cash.items.length">Change not required</template>
                <template v-if="payment_data.change?.account && table_cash.items.length">
                  Change provided {{ formatAmount(payment_data.change.amount) }} {{ payment_data.change.currency.code }} from {{ payment_data.change.account.name }}
                </template>
                <template v-if="payment_data.change && !payment_data.change.account && table_cash.items.length">
                  Change pending {{ formatAmount(payment_data.change.amount) }} {{ payment_data.change.currency.code }}
                </template>
              </p>
            </div>
          </app-cells>
        </template>
        <template #item-2>
          <app-cells>
            <h2 class="title">Other</h2>
            <app-button size="small" @click.native="onShowModal(null, 'Other', 'editModal')">Add other</app-button>
          </app-cells>
        </template>
      </app-grid>
      <app-grid :indent="true">
        <template #item-1>
          <v-client-table
            :data="table_cash.items"
            :columns="table_cash.columns"
            :options="table_cash.options"
            class="table-default"
          >
            <template #h__cashflow><div class="table-default__left">Cashflow</div></template>
            <template #cashflow="{ row }">
              <div class="table-default__left">
                <span v-if="row.cashflow">{{ row.cashflow.account.name }}</span>
                <button v-else @click="onShowAccountModal(row)">
                  <img src="@/assets/img/add-big-button-icon.svg" alt="Add">
                </button>
              </div>
            </template>
            <template #amount="{ row }">{{ formatAmount(row.amount) }} {{ row.currency.code }}</template>
            <template #controls="{ row }">
              <div class="table-default__controls">
                <button @click="onShowModal(row, 'Cash', 'editModal')">
                  <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
                </button>
                <button @click="onShowModal(row, 'Cash', 'deleteModal')">
                  <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
                </button>
              </div>
            </template>
          </v-client-table>
        </template>
        <template #item-2>
          <v-client-table
            :data="table_other.items"
            :columns="table_other.columns"
            :options="table_other.options"
            class="table-default"
          >
            <template #amount="{ row }">{{ formatAmount(row.amount) }} {{ row.currency.code }}</template>
            <template #controls="{ row }">
              <div class="table-default__controls">
                <button @click="onShowModal(row, 'Other', 'editModal')">
                  <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
                </button>
                <button @click="onShowModal(row, 'Other', 'deleteModal')">
                  <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
                </button>
              </div>
            </template>
          </v-client-table>
        </template>
      </app-grid>
      <app-cells>
        <h2 class="title">Card</h2>
        <app-button size="small" @click.native="onShowModal(null, 'CC', 'editModal')">Add card</app-button>
      </app-cells>
      <v-client-table
        :data="table_card.items"
        :columns="table_card.columns"
        :options="table_card.options"
        class="table-default table-default--indent"
      >
        <template #rrn="{ row }">
          <span v-if="!row.rrn" class="status-tag" :style="{ 'backgroundColor': '#e85c20' }">Missing</span>
          <template v-else>{{ row.rrn }}</template>
        </template>
        <template #bank="{ row }">
          {{ row.bank.short_name }} ({{ row.channel.name }})
        </template>
        <template #slip="{ row }">
          <a
            v-if="row.machine_slip"
            :href="row.machine_slip.file"
            target="_blank"
            class="status-tag"
            style="background-color: #439ad1"
          >
            View
          </a>
          <template v-else>-</template>
        </template>
        <template #amount="{ row }">{{ formatAmount(row.amount) }} $</template>
        <template #commission="{ row }">{{ formatAmount(row.commission) }} $</template>
        <template #settlement_amount="{ row }">{{ formatAmount(row.settlement_amount) }} $</template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowModal(row, 'CC', 'editModal')">
              <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
            </button>
            <button @click="onShowModal(row, 'CC', 'deleteModal')">
              <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-grid :indent="true">
        <template #item-1>
          <app-cells>
            <h2 class="title">Gear</h2>
            <app-button size="small" @click.native="onShowModal(null, 'Gear', 'editModal')">Add gear</app-button>
          </app-cells>
          <v-client-table
            :data="table_gear.items"
            :columns="table_gear.columns"
            :options="table_gear.options"
            class="table-default"
          >
            <template #h__client><div class="table-default__left">Client</div></template>
            <template #client="{ row }"><div class="table-default__left">{{ row.client.full_name }}</div></template>
            <template #checklist="{ row }">
              <a
                v-if="row.attachment"
                :href="row.attachment.file"
                target="_blank"
                class="status-tag"
                style="background-color: #439ad1"
              >
                View
              </a>
              <template v-else>-</template>
            </template>
            <template #amount="{ row }">{{ formatAmount(row.amount) }} $</template>
            <template #rental="{ row }">
              <div class="table-default__controls">
                <router-link
                  v-if="row.rental"
                  :to="{ name: 'rentals-detail', params: { id: row.rental.id } }"
                  class="table-default__link-border"
                >
                  Rental - {{ row.rental.id }}
                </router-link>
                <span v-else class="status-tag" style="background-color: #e85c20">Missing</span>
              </div>
            </template>
            <template #controls="{ row }">
              <div class="table-default__controls">
                <button @click="onShowModal(row, 'Gear', 'editModal')">
                  <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
                </button>
                <button @click="onShowModal(row, 'Gear', 'deleteModal')">
                  <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
                </button>
              </div>
            </template>
          </v-client-table>
        </template>
        <template #item-2>
          <app-cells>
            <h2 class="title">Additional</h2>
            <app-button size="small" @click.native="onShowModal(null, 'Additional', 'editModal')">Add additional</app-button>
          </app-cells>
          <v-client-table
            :data="table_additional.items"
            :columns="table_additional.columns"
            :options="table_additional.options"
            class="table-default"
          >
            <template #h__client><div class="table-default__left">Client</div></template>
            <template #client="{ row }"><div class="table-default__left">{{ row.client.full_name }}</div></template>
            <template #amount="{ row }">{{ formatAmount(row.amount) }} $</template>
            <template #controls="{ row }">
              <div class="table-default__controls">
                <button @click="onShowModal(row, 'Additional', 'editModal')">
                  <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
                </button>
                <button @click="onShowModal(row, 'Additional', 'deleteModal')">
                  <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
                </button>
              </div>
            </template>
          </v-client-table>
        </template>
      </app-grid>
      <app-cells><h2 class="title">Clients</h2></app-cells>
      <v-client-table
        :data="table_clients.items"
        :columns="table_clients.columns"
        :options="table_clients.options"
        class="table-default"
      >
        <template #h__full_name><div class="table-default__left">Full name</div></template>
        <template #full_name="{ row }">
          <div class="table-default__left table-default__controls">
            <router-link :to="{name: 'clients-detail', params: {id: row.id }}" class="table-default__link-border">
              {{ row.full_name }}
            </router-link>
            <img v-if="row.category && row.category.code === 'tour_leader'" src="@/assets/img/tour-leader-icon.svg" alt="Leader">
            <img v-if="row.group.is_primary_contact" src="@/assets/img/bookmark-icon.svg" alt="Primary">
          </div>
        </template>
        <template #transport="{ row }">
          <div class="table-default__controls">
            <button
              @click="onCopyTransportData(row.transport.arrival, 'arrival')"
              v-tooltip="{ content: tooltipTransportContent(row.transport.arrival, 'arrival'), placement: 'top-start', offset: 10, html: true }"
              class="table-default__transport table-default__transport--arrival"
              :class="{
              'table-default__transport--present': row.transport.arrival.arrival_method,
              'table-default__transport--confirmed': row.transport.arrival.arrival_confirmed,
              'table-default__transport--car': row.transport.arrival.arrival_method?.code === 'car',
              'table-default__transport--bus': row.transport.arrival.arrival_method?.code === 'bus',
              'table-default__transport--self': row.transport.arrival.arrival_method?.code === 'self'
            }"
            />
            <button
              @click="onCopyTransportData(row.transport.departure, 'departure')"
              v-tooltip="{ content: tooltipTransportContent(row.transport.departure, 'departure'), placement: 'top-start', offset: 10, html: true }"
              class="table-default__transport table-default__transport--departure"
              :class="{
              'table-default__transport--present': row.transport.departure.departure_method,
              'table-default__transport--confirmed': row.transport.departure.departure_confirmed,
              'table-default__transport--car': row.transport.departure.departure_method?.code === 'car',
              'table-default__transport--bus': row.transport.departure.departure_method?.code === 'bus',
              'table-default__transport--self': row.transport.departure.departure_method?.code === 'self'
            }"
            />
          </div>
        </template>
        <template #climbing="{ row }">
          <div class="table-default__controls">
            <router-link
              v-if="row.climbing.length === 1"
              v-tooltip="{ content: tooltipClimbingContent(row.climbing[0].group), placement: 'top-start', offset: 10, html: true }"
              :to="{name: 'climbing-groups-detail', params: {id: row.climbing[0].group.id}}"
              class="table-default__activity table-default__activity--climbing table-default__activity--active"
            ></router-link>
            <button
              v-else
              @click="onShowActivitiesModal(row, 'climbing')"
              class="table-default__activity table-default__activity--climbing"
              :class="{'table-default__activity--active': row.climbing.length}"
            ></button>
            <button
              @click="onCopyInsuranceData(row.climbing_insurance, 'climbing')"
              v-tooltip="{content: tooltipInsuranceContent(row.climbing_insurance, 'climbing'), placement: 'top-start', offset: 10, html: true}"
              class="table-default__insurance"
              :class="{ 'table-default__insurance--climbing': row.climbing_insurance.climbing_insurance_company }"
            ></button>
            <template v-if="row.climbing.length === 1">
              <a
                v-if="is_general_manager || is_sales_manager || is_sales_supervisor || is_operations_supervisor"
                :href="`${family_url}${row.hash}/rentals/${row.climbing[0].group.id}`"
                target="_blank"
                v-tooltip="{content: tooltipChecklistContent(row.climbing[0].checklist), placement: 'top-start', offset: 10, html: true}"
                class="table-default__activity table-default__activity--checklist table-default__activity--active table-default__activity--adds"
                :class="{
                'table-default__activity--checklist-total': row.climbing[0].checklist.completed && row.climbing[0].checklist.total_price === 0,
                'table-default__activity--checklist-time': !row.climbing[0].checklist.completed,
                'table-default__activity--checklist-complete': row.climbing[0].checklist.completed && row.climbing[0].checklist.total_price > 0
              }"
              ></a>
              <span
                v-if="!is_general_manager && !is_sales_manager && !is_sales_supervisor && !is_operations_supervisor"
                v-tooltip="{content: tooltipChecklistContent(row.climbing[0].checklist), placement: 'top-start', offset: 10, html: true}"
                class="table-default__activity table-default__activity--checklist table-default__activity--active table-default__activity--adds"
                :class="{
                'table-default__activity--checklist-total': row.climbing[0].checklist.completed && row.climbing[0].checklist.total_price === 0,
                'table-default__activity--checklist-time': !row.climbing[0].checklist.completed,
                'table-default__activity--checklist-complete': row.climbing[0].checklist.completed && row.climbing[0].checklist.total_price > 0
              }"
              ></span>
            </template>
            <button
              v-else
              @click="onShowActivitiesModal(row, 'climbing')"
              class="table-default__activity table-default__activity--checklist"
              :class="{
                'table-default__activity--active': row.climbing.length,
              }"
            ></button>
            <a
              :href="`${family_url}${row.hash}/medical`"
              target="_blank"
              class="table-default__activity table-default__activity--medical-climbing table-default__activity--adds"
              :class="{
              'table-default__activity--active': row.climbing.length,
              'table-default__activity--medical-climbing-clock': !row.medical_clearance,
              'table-default__activity--medical-climbing-complete': row.medical_clearance,
              }"
            ></a>
          </div>
        </template>
        <template #safari="{ row }">
          <div class="table-default__controls">
            <router-link
              v-if="row.safari.length === 1"
              v-tooltip="{ content: tooltipSafariContent(row.safari[0].group), placement: 'top-start', offset: 10, html: true }"
              :to="{name: 'safari-groups-detail', params: {id: row.safari[0].group.id}}"
              class="table-default__activity table-default__activity--safari table-default__activity--active"
            ></router-link>
            <button
              v-else
              @click="onShowActivitiesModal(row, 'safari')"
              class="table-default__activity table-default__activity--safari"
              :class="{'table-default__activity--active': row.safari.length}"
            ></button>
            <button
              @click="onCopyInsuranceData(row.safari_insurance, 'safari')"
              v-tooltip="{content: tooltipInsuranceContent(row.safari_insurance, 'safari'), placement: 'top-start', offset: 10, html: true}"
              class="table-default__insurance"
              :class="{ 'table-default__insurance--safari': row.safari_insurance.safari_insurance_company }"
            ></button>
            <a
              v-if="row.safari.length === 1"
              :href="`${family_url}${row.hash}/snacks/${row.safari[0].hash}`"
              target="_blank"
              v-tooltip="{content: tooltipSnackBarContent(row.safari[0]), placement: 'top-start', offset: 10, html: true}"
              class="table-default__activity table-default__activity--snacks table-default__activity--adds table-default__activity--active"
              :class="{
              'table-default__activity--snacks-time': !row.safari[0].snack_bar.completed,
              'table-default__activity--snacks-no-need': row.safari[0].snack_bar.no_need,
              'table-default__activity--snacks-usd': row.safari[0].snack_bar.completed && row.safari[0].snack_bar.outstanding_amount > 0,
              'table-default__activity--snacks-complete': row.safari[0].snack_bar.completed && row.safari[0].snack_bar.outstanding_amount === 0
            }"
            ></a>
            <button
              v-else
              @click="onShowActivitiesModal(row, 'safari')"
              class="table-default__activity table-default__activity--snacks"
              :class="{
                'table-default__activity--active': row.safari.length,
              }"
            ></button>
          </div>
        </template>
        <template #day_trips="{ row }">
          <div class="table-default__controls">
            <router-link
              v-if="row.day_trips.length === 1"
              v-tooltip="{ content: tooltipDayTripsContent(row.day_trips[0]), placement: 'top-start', offset: 10, html: true }"
              :to="{name: 'day-trips-groups-detail', params: {id: row.day_trips[0].id}}"
              class="table-default__activity table-default__activity--day_trips table-default__activity--active table-default__activity--adds"
              :class="{ 'table-default__activity--medical-climbing-clock': row.day_trips[0]?.pick_up_time_to_be_confirmed }"
            ></router-link>
            <button
              v-else
              @click="onShowActivitiesModal(row, 'day_trips')"
              class="table-default__activity table-default__activity--day_trips table-default__activity--adds"
              :class="{
              'table-default__activity--active': row.day_trips.length,
              'table-default__activity--medical-climbing-clock': row.day_trips[0]?.pick_up_time_to_be_confirmed,
            }"
            ></button>
            <router-link
              v-if="row.island_vacation_groups.length === 1"
              v-tooltip="{ content: tooltipIslandContent(row.island_vacation_groups[0]), placement: 'top-start', offset: 10, html: true }"
              :to="{name: 'island-vacation-groups-detail-clients', params: {id: row.island_vacation_groups[0].id}}"
              class="table-default__activity table-default__activity--island table-default__activity--active table-default__activity--adds"
            ></router-link>
            <button
              v-else
              @click="onShowActivitiesModal(row, 'island_vacation_groups')"
              class="table-default__activity table-default__activity--island table-default__activity--adds"
              :class="{
              'table-default__activity--active': row.island_vacation_groups.length,
            }"
            ></button>
            <router-link
              v-if="row.free_days.length === 1"
              v-tooltip="{ content: tooltipFreeDaysContent(row.free_days[0]), placement: 'top-start', offset: 10, html: true }"
              :to="{name: 'free-days-detail', params: {id: row.free_days[0].id}}"
              class="table-default__activity table-default__activity--free_days table-default__activity--active table-default__activity--adds"
            ></router-link>
            <button
              v-else
              @click="onShowActivitiesModal(row, 'free_days')"
              class="table-default__activity table-default__activity--free_days table-default__activity--adds"
              :class="{
              'table-default__activity--active': row.free_days.length,
            }"
            ></button>
          </div>
        </template>
      </v-client-table>
      <app-cells><h2 class="title">Invoices</h2></app-cells>
      <v-client-table
        :data="table_invoices.items"
        :columns="table_invoices.columns"
        :options="table_invoices.options"
        class="table-default table-default--indent"
      >
        <template #h__number><div class="table-default__left">Invoice #</div></template>
        <template #h__payer><div class="table-default__left">Payer</div></template>
        <template #h__method><div class="table-default__left">Method</div></template>
        <template #h__status><div class="table-default__left">Status</div></template>
        <template #number="{ row }">
          <div class="table-default__controls table-default__left">
            <a
              :href="`https://story.altezza.travel/changeInvoice/${row.story_id}`"
              target="_blank"
              class="table-default__link-border"
            >
              {{ row.number }}
            </a>
          </div>
        </template>
        <template #payer="{ row }">
          <div class="table-default__left">{{ row.payer }}</div>
        </template>
        <template #status="{ row }">
          <div class="table-default__left table-default__controls">
          <span class="status-tag" :style="{ 'backgroundColor': row.status.color }">
            {{ row.status.name }}
          </span>
          </div>
        </template>
        <template #deadline="{ row }">
          {{ dateFormat(row.deadline) }}
        </template>
        <template #amount="{ row }">
          {{ row.amount_payable ? `${formatAmount(row.amount_payable)} ${row.currency.code}` : '-'}}
        </template>
        <template #payable="{ row }">
          {{ row.amount_payable_usd ? `${formatAmount(row.amount_payable_usd)} USD` : '-'}}
        </template>
        <template #method="{ row }">
          <div class="table-default__left">
            {{ row.payment_method ? `${row.payment_method.name} ${dateFormat(row.date_of_payment)}` : '-' }}
          </div>
        </template>
      </v-client-table>
      <template v-if="payment_data.attachments.length">
        <app-cells><h2 class="title">Attachments</h2></app-cells>
        <VueGallery :images="gallery_images" :index="gallery_index" @close="gallery_index = null" />
        <div class="gallery">
          <div
            class="gallery__preview"
            v-for="(image, imageIndex) in gallery_images"
            :key="imageIndex"
          >
            <img :src="image" @click="gallery_index = imageIndex" alt="Preview">
          </div>
        </div>
      </template>
      <div class="downloads">
        <div class="downloads__item" :class="{'downloads__item--loading': payment_completed_loader}">
          <div class="downloads__left">
            <div class="downloads__info">
              <span class="downloads__name downloads__name--no-events">Completed</span>
            </div>
          </div>
          <div class="downloads__right">
            <div class="downloads__values">
              <span class="downloads__key">Status:</span>
              <app-switcher
                v-model="payment_data.payment_control.completed"
                :readonly="payment_data.payment_control.completed"
                @change="onUpdatePaymentData"
              />
            </div>
            <template v-if="payment_data.payment_control.completed && !payment_completed_loader">
              <div class="downloads__values">
                <span class="downloads__key">Completed at:</span>
                <span class="downloads__value downloads__width">{{ dateTimeFormat(payment_data.payment_control.completed_at) }}</span>
              </div>
              <div class="downloads__values">
                <span class="downloads__key">Completed by:</span>
                <div class="downloads__person downloads__width">
                  <div class="downloads__photo">
                    <img :src="getUserAvatar(payment_data.payment_control.completed_by?.crop.crop_url)" alt="Person">
                  </div>
                  <span class="downloads__value">{{ payment_data.payment_control.completed_by?.full_name || '-' }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>
    <modal
      name="activities-modal"
      :width="800"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideModalActivities" class="popup__close"></button>
        <div class="popup__title popup__title--flex">
          <template v-if="activities_modal_type === 'climbing'">
            <img src="@/assets/img/climbing-modal-icon.svg" alt="Climbing">
            <span>Climbing</span>
          </template>
          <template v-if="activities_modal_type === 'safari'">
            <img src="@/assets/img/safari-modal-icon.svg" alt="Safari">
            <span>Safari</span>
          </template>
          <template v-if="activities_modal_type === 'day_trips'">
            <img src="@/assets/img/day-trips-modal-icon.svg" alt="Day trips">
            <span>Day trip</span>
          </template>
          <template v-if="activities_modal_type === 'free_days'">
            <img src="@/assets/img/free-days-modal-icon.svg" alt="Free days">
            <span>Free days</span>
          </template>
          <template v-if="activities_modal_type === 'island_vacation_groups'">
            <img src="@/assets/img/island-modal-icon.svg" alt="Free days">
            <span>Island vacation </span>
          </template>
        </div>
        <div class="popup__body">
          <div class="popup__activities">
            <div
              v-for="activity of activities_modal_data"
              :key="activity.id"
              class="popup__activity"
            >
              <div class="popup__activity-info">
                <template v-if="['day_trips', 'climbing'].includes(activities_modal_type)">
                  <div class="popup__activity-row popup__activity-row--single">
                    <div class="popup__activity-values">
                      <span>Group Name:</span>
                      <router-link
                        v-if="activities_modal_type === 'climbing'"
                        :to="{name: 'climbing-groups-detail', params: {id: activity.group.id}}"
                        class="popup__activity-link"
                      >
                        {{ activity.name }}
                      </router-link>
                      <router-link
                        v-if="activities_modal_type === 'day_trips'"
                        :to="{name: 'day-trips-groups-detail', params: {id: activity.id}}"
                        class="popup__activity-link"
                      >
                        {{ activity.name }}
                      </router-link>
                    </div>
                  </div>
                </template>
                <template v-if="activities_modal_type === 'safari'">
                  <div class="popup__activity-row popup__activity-row--single">
                    <div class="popup__activity-values">
                      <span>Group Name:</span>
                      <router-link
                        :to="{name: 'safari-groups-detail', params: {id: activity.group.id}}"
                        class="popup__activity-link"
                      >
                        {{ activity.group.name }}
                      </router-link>
                    </div>
                  </div>
                </template>
                <div v-if="activities_modal_type === 'climbing'" class="popup__activity-row">
                  <div class="popup__activity-values">
                    <span>Mount:</span>
                    <span>{{ activity.group.route.mountain.name }}</span>
                  </div>
                  <div class="popup__activity-values">
                    <span>Route:</span>
                    <span>{{ activity.group.route.name }}</span>
                  </div>
                </div>
                <div v-if="activities_modal_type === 'day_trips'" class="popup__activity-row popup__activity-row--single">
                  <div class="popup__activity-values popup__activity-values--center">
                    <span>Date:</span>
                    <span>{{ dateFormat(activity.date) }} {{ activity.pick_up_time }}</span>
                    <span
                      v-if="activity.pick_up_time_to_be_confirmed"
                      class="status-tag"
                      :style="{ 'backgroundColor': '#f0c427', 'color': '#fff' }"
                    >
                      To be confirmed
                    </span>
                  </div>
                </div>
                <div v-if="activities_modal_type === 'day_trips'" class="popup__activity-row">
                  <div class="popup__activity-values">
                    <span>Pick-up:</span>
                    <span>{{ activity.pick_up_location ? activity.pick_up_location.short_name : '-' }}</span>
                  </div>
                </div>
                <div v-if="activities_modal_type === 'day_trips'" class="popup__activity-row">
                  <div class="popup__activity-values">
                    <span>Activity:</span>
                    <span>{{ activity.activity.short_name }}</span>
                  </div>
                </div>
                <div v-if="activities_modal_type === 'safari'" class="popup__activity-row">
                  <div class="popup__activity-values">
                    <span>Tour start:</span>
                    <span>{{ dateFormat(activity.group.tour_start) }}</span>
                  </div>
                  <div class="popup__activity-values">
                    <span>Tour finish:</span>
                    <span>{{ dateFormat(activity.group.tour_end) }}</span>
                  </div>
                  <div class="popup__activity-values">
                    <span>Duration:</span>
                    <span>{{ activity.group.duration }} days</span>
                  </div>
                </div>
                <div v-if="activities_modal_type === 'climbing'" class="popup__activity-row">
                  <div class="popup__activity-values">
                    <span>Tour start:</span>
                    <span>{{ dateFormat(activity.group.tour_start) }}</span>
                  </div>
                  <div class="popup__activity-values">
                    <span>Tour finish:</span>
                    <span>{{ dateFormat(activity.group.tour_end) }}</span>
                  </div>
                  <div class="popup__activity-values">
                    <span>Duration:</span>
                    <span>{{ activity.group.route.duration }} days</span>
                  </div>
                </div>
                <template v-if="activities_modal_type === 'island_vacation_groups'">
                  <div class="popup__activity-row">
                    <div class="popup__activity-values">
                      <span>Group name:</span>
                      <span>{{ activity.name }}</span>
                    </div>
                  </div>
                  <div class="popup__activity-row">
                    <div class="popup__activity-values">
                      <span>Tour start:</span>
                      <span>{{ dateFormat(activity.tour_start) }}</span>
                    </div>
                  </div>
                  <div class="popup__activity-row">
                    <div class="popup__activity-values">
                      <span>Tour end:</span>
                      <span>{{ dateFormat(activity.tour_end) }}</span>
                    </div>
                  </div>
                </template>
                <template v-if="activities_modal_type === 'free_days'">
                  <div class="popup__activity-row">
                    <div class="popup__activity-values">
                      <span>From:</span>
                      <span>{{ dateFormat(activity.tour_start) }}</span>
                    </div>
                    <div class="popup__activity-values">
                      <span>To:</span>
                      <span>{{ dateFormat(activity.tour_end) }}</span>
                    </div>
                  </div>
                  <div class="popup__activity-row popup__activity-row--single">
                    <div class="popup__activity-values">
                      <span>Location:</span>
                      <span>{{ activity.location.short_name }}</span>
                    </div>
                  </div>
                </template>
                <template v-if="activities_modal_type === 'transfers'">
                  <div class="popup__activity-row popup__activity-row--double">
                    <div class="popup__activity-values">
                      <span>Pick-up date:</span>
                      <span>{{ dateFormat(activity.pick_up_date) }} {{ activity.pick_up_time }}</span>
                    </div>
                    <div class="popup__activity-values">
                      <span>Pick-up point:</span>
                      <span>{{ activity.pick_up_point?.short_name }}</span>
                    </div>
                  </div>
                  <div class="popup__block-route popup__activity-row">
                    <span class="popup__activity-key">Points:</span>
                    <span>{{ activity.pick_up_point.short_name }}</span>
                    <img src="@/assets/img/arrow-right-gray-icon.svg" alt="">
                    <span v-for="(point, index) in activity.points" :key="point.id" class="popup__block-route-group">
                      <span>{{ point.location.short_name }}</span>
                      <img v-if="index !== activity.points.length - 1" src="@/assets/img/arrow-right-gray-icon.svg" alt="">
                    </span>
                  </div>
                  <div class="popup__activity-row popup__activity-row--single">
                    <div class="popup__activity-values popup__activity-values--center">
                      <span>Status:</span>
                      <span
                        class="status-tag"
                        :style="{ 'backgroundColor': activity.status.code === 'need_amended' || activity.status.code === 'requested' ? '#eec82d' : activity.status.code === 'arranged' ? '#77b017' : '#bcbcbc', 'color': '#fff' }"
                      >
                        {{ activity.status.name }}
                      </span>
                    </div>
                  </div>
                </template>
              </div>
              <div v-if="activities_modal_type === 'climbing'" class="popup__adds">
                <div class="popup__add">
                  <div class="popup__add-name">
                    <img src="@/assets/img/checklist-icon.svg" alt="Checklist">
                    <span>Equipment checklist</span>
                    <a
                      v-if="is_general_manager || is_sales_manager || is_sales_supervisor"
                      :href="`${family_url}${client_hash}/rentals/${activity.group.id}`"
                      target="_blank"
                      class="popup__activity-external"
                    ></a>
                  </div>
                  <img v-if="activity.checklist.completed" src="@/assets/img/modal-complete-icon.svg" alt="Complete">
                  <img v-else src="@/assets/img/modal-out-icon.svg" alt="Out">
                </div>
              </div>
              <div v-if="activities_modal_type === 'safari'" class="popup__add popup__add--col">
                <div class="popup__add-top">
                  <div class="popup__add-name">
                    <img src="@/assets/img/food-modal-icon.svg" alt="Services">
                    <span>Snack bar</span>
                    <a
                      :href="`${family_url}${client_hash}/snacks/${activity.hash}`"
                      target="_blank"
                      class="popup__activity-external"
                    ></a>
                  </div>
                  <div class="popup__add-info">
                    <img v-if="!activity.snack_bar" src="@/assets/img/clock-snacks.svg" alt="Out">
                    <template v-else>
                      <img v-if="!activity.snack_bar.completed" src="@/assets/img/modal-notcomplete-icon.svg" alt="Notcomplete">
                      <img v-if="activity.snack_bar.no_need" src="@/assets/img/modal-noneed-icon.svg" alt="Noneed">
                      <img v-if="activity.snack_bar.completed && activity.snack_bar.outstanding_amount === 0" src="@/assets/img/modal-complete-icon.svg" alt="Complete">
                      <img v-if="activity.snack_bar.completed && activity.snack_bar.outstanding_amount > 0" src="@/assets/img/modal-usd-icon.svg" alt="USD">
                    </template>
                  </div>
                </div>
                <div v-if="activity.snack_bar.completed && activity.snack_bar.outstanding_amount >= 0" class="popup__add-values">
                  <div class="popup__add-value">
                    <span>Items:</span>
                    <span>{{ activity.snack_bar.items_count }}</span>
                  </div>
                  <div class="popup__add-value">
                    <span>Order amount:</span>
                    <span>{{ activity.snack_bar.order_amount.toLocaleString() }}$</span>
                  </div>
                  <div class="popup__add-value">
                    <span>Credit:</span>
                    <span>{{ activity.snack_bar.credit.toLocaleString() }}$</span>
                  </div>
                  <div v-if="activity.snack_bar.completed && activity.snack_bar.outstanding_amount > 0" class="popup__add-value">
                    <span>Outstanding amount:</span>
                    <span>{{ activity.snack_bar.outstanding_amount.toLocaleString() }}$</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </modal>
    <modal
      name="editModal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideModal('editModal')" class="popup__close"></button>
        <div class="popup__title">{{ item_to_action ? 'Edit' : 'Add' }} item</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Currency" required v-if="type_to_action === 'Cash' || type_to_action === 'Other'">
              <app-cells :indent="false">
                <app-radio
                  v-for="radio of currency_options"
                  :key="radio.label"
                  v-model="form.currency"
                  name="filterCurrency"
                  :value="radio.value"
                  :checked="form.currency === radio.value"
                >
                  <template #label>{{ radio.label }}</template>
                </app-radio>
              </app-cells>
            </app-form-group>
            <app-form-group
              label="Client"
              required
              v-if="type_to_action === 'Additional' || (type_to_action === 'Gear' && !item_to_action)">
              <app-select
                v-model="form.client"
                :options="client_options"
                :reduce="client => client.id"
                :clearable="false"
                label="full_name"
                placeholder="Select client"
                :error="$v.form.client.$error"
              />
              <template #error>
                <div v-if="$v.form.client.$dirty && !$v.form.client.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group
              :label="type_to_action === 'Cash' || type_to_action === 'Other' ? 'Amount' : 'Amount ($)'"
              label-for="amount"
              required
            >
              <app-fin-number
                v-model="form.amount"
                :error="$v.form.amount.$error"
                id="amount"
                name="amount"
                placeholder="0"
              />
              <template #error>
                <div v-if="$v.form.amount.$dirty && !$v.form.amount.required">Required field</div>
              </template>
            </app-form-group>
            <template v-if="type_to_action === 'CC'">
              <app-form-group label="Bank" required>
                <app-cells :indent="false">
                  <app-radio
                    v-for="radio of bank_options"
                    :key="radio.id"
                    v-model="form.bank"
                    :value="radio.id"
                    name="filterBank"
                    :checked="form.bank === radio.id"
                  >
                    <template #label>{{ radio.short_name }}</template>
                  </app-radio>
                </app-cells>
              </app-form-group>
              <app-form-group label="Channel" required>
                <app-cells :indent="false">
                  <app-radio
                    v-for="radio of channel_options"
                    :key="radio.code"
                    v-model="form.channel"
                    :value="radio.code"
                    name="filterCreditCard"
                    :checked="form.channel === radio.code"
                  >
                    <template #label>{{ radio.name }}</template>
                  </app-radio>
                </app-cells>
              </app-form-group>
              <app-form-group label="RRN" required label-for="rrn">
                <app-input
                  v-model="form.rrn"
                  :error="$v.form.rrn.$error"
                  id="rrn"
                  name="rrn"
                  placeholder="RRN"
                  type="number"
                  inputmode="numeric"
                />
                <template #error>
                  <div v-if="$v.form.rrn.$dirty && !$v.form.rrn.required">Required field</div>
                  <div v-if="$v.form.rrn.$dirty && !$v.form.rrn.maxLength">Length of RRN must be equal to 12</div>
                  <div v-if="$v.form.rrn.$dirty && !$v.form.rrn.minLength">Length of RRN must be equal to 12</div>
                </template>
              </app-form-group>
              <app-form-group label="Machine slip" required>
                <app-uploader
                  v-model="form.machine_slip"
                  :extensions="['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/zip', 'application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'image/bmp']"
                  formDataFormats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF"
                />
                <template #error>
                  <div v-if="$v.form.machine_slip.$dirty && !$v.form.machine_slip.required">Required field</div>
                </template>
              </app-form-group>
            </template>
            <app-form-group
              label="Description"
              required label-for="description"
              v-if="type_to_action === 'Additional' || type_to_action === 'Other'">
              <app-input
                v-model="form.description"
                :error="$v.form.description.$error"
                id="description"
                name="description"
                placeholder="Description"
              />
              <template #error>
                <div v-if="$v.form.description.$dirty && !$v.form.description.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submitEdit">{{ item_to_action ? 'Save' : 'Add' }}</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="deleteModal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideModal('deleteModal')" class="popup__close"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete item
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this item?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onHideModal('deleteModal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteItem" ref="deleteItem">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="accountModal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideModal('accountModal')" class="popup__close"></button>
        <div class="popup__title popup__title--flex">Receiving cash</div>
        <div class="popup__subt">
          Are you sure you want to add {{ formatAmount(item_to_action?.amount) }} {{ item_to_action?.currency?.code }} Safe?
        </div>
        <app-cells position="center" :indent="false">
          <app-button type="submit" ref="postAccount" @click.native="sendAccount">Yes</app-button>
        </app-cells>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteFinancePaymentsOnSiteAdditional,
  deleteFinancePaymentsOnSiteCash,
  deleteFinancePaymentsOnSiteGear,
  deleteFinancePaymentsOnSiteOther,
  deleteFinancePaymentsOnSitePos,
  getCardTypes,
  getClients,
  getClientsGroup,
  getClientsLite,
  getFinanceBanks,
  getFinanceInvoiceList,
  getFinancePaymentsOnSite,
  getFinancePaymentsOnSiteAdditionalList,
  getFinancePaymentsOnSiteCashList,
  getFinancePaymentsOnSiteGearList,
  getFinancePaymentsOnSiteOtherList,
  getFinancePosTransactionsList,
  patchFinancePaymentsOnSiteAdditional,
  patchFinancePaymentsOnSiteCash,
  patchFinancePaymentsOnSiteCashCashflow,
  patchFinancePaymentsOnSiteGear,
  patchFinancePaymentsOnSiteOther,
  patchFinancePosTransactions,
  postFinancePaymentsOnSiteAdditional,
  postFinancePaymentsOnSiteCash,
  postFinancePaymentsOnSiteComplete,
  postFinancePaymentsOnSiteGear,
  postFinancePaymentsOnSiteOther,
  postFinancePaymentsOnSitePos,
} from '@/http'
import { format, parseISO } from 'date-fns'
import { maxLength, minLength, required, requiredIf } from 'vuelidate/lib/validators'
import AppColoredNotes from '@/components/AppColoredNotes.vue'
import VueGallery from 'vue-gallery'

const action_map = {
  Cash: {
    delete: deleteFinancePaymentsOnSiteCash,
    patch: patchFinancePaymentsOnSiteCash,
    post: postFinancePaymentsOnSiteCash,
  },
  Gear: {
    delete: deleteFinancePaymentsOnSiteGear,
    patch: patchFinancePaymentsOnSiteGear,
    post: postFinancePaymentsOnSiteGear,
  },
  Additional: {
    delete: deleteFinancePaymentsOnSiteAdditional,
    patch: patchFinancePaymentsOnSiteAdditional,
    post: postFinancePaymentsOnSiteAdditional,
  },
  Other: {
    delete: deleteFinancePaymentsOnSiteOther,
    patch: patchFinancePaymentsOnSiteOther,
    post: postFinancePaymentsOnSiteOther,
  },
  CC: {
    delete: deleteFinancePaymentsOnSitePos,
    patch: patchFinancePosTransactions,
    post: postFinancePaymentsOnSitePos,
  },
}

export default {
  name: 'FinanceOnSiteDetail',
  components: {AppColoredNotes, VueGallery},
  data() {
    return {
      gallery_index: null,
      family_url: process.env.VUE_APP_FAMILY,
      page_loaded: false,
      payment_completed_loader: false,
      payment_data: {},
      group_data: {},
      activities_modal_data: [],
      activities_modal_type: '',
      medical_clearance_present: false,
      client_hash: '',
      item_to_action: null,
      type_to_action: '',
      form: {},
      client_options: [],
      account_options: [],
      bank_options: [],
      channel_options: [],
      table_cash: {
        items: [],
        columns: [
          'cashflow',
          'amount',
          'controls',
        ],
        options: {
          headings: {
            'cashflow': 'Cashflow',
            'amount': 'Amount',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_card: {
        items: [],
        columns: [
          'rrn',
          'bank',
          'slip',
          'amount',
          'commission',
          'settlement_amount',
          'controls',
        ],
        options: {
          headings: {
            'rrn': 'RRN',
            'bank': 'Bank (Channel)',
            'slip': 'Slip',
            'amount': 'Amount',
            'commission': 'Commission',
            'settlement_amount': 'Settlement',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_other: {
        items: [],
        columns: [
          'amount',
          'description',
          'controls',
        ],
        options: {
          headings: {
            'amount': 'Amount',
            'description': 'Description',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_gear: {
        items: [],
        columns: [
          'client',
          'checklist',
          'amount',
          'rental',
          'controls',
        ],
        options: {
          headings: {
            'client': 'Client',
            'checklist': 'Checklist',
            'amount': 'Amount',
            'rental': 'Rental',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_additional: {
        items: [],
        columns: [
          'client',
          'amount',
          'description',
          'controls',
        ],
        options: {
          headings: {
            'client': 'Client',
            'amount': 'Amount',
            'description': 'Description',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_clients: {
        items: [],
        columns: ['full_name', 'transport', 'climbing', 'safari', 'day_trips'],
        options: {
          headings: {
            full_name: 'Full name',
            transport: 'Transport',
            climbing: 'Climbing',
            safari: 'Safari',
            day_trips: 'Activities',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_invoices: {
        items: [],
        columns: [
          'number',
          'payer',
          'status',
          'deadline',
          'amount',
          'payable',
          'method',
        ],
        options: {
          headings: {
            number: 'Invoice #',
            payer: 'Payer',
            status: 'Status',
            deadline: 'Deadline',
            amount: 'Amount',
            payable: 'Payable',
            method: 'Method',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      currency_options: [
        { value: 'USD', label: 'USD ($)' },
        { value: 'TZS', label: 'TZS (TSh)' },
        { value: 'EUR', label: 'EUR (€)' },
        { value: 'GBP', label: 'GBP (£)' },
      ],
    }
  },
  validations: {
    form: {
      amount: { required },
      client: {
        required: requiredIf(function() {
          return (this.type_to_action === 'Gear' && !this.item_to_action) || this.type_to_action === 'Additional'
        })
      },
      rrn: {
        required: requiredIf(function() {
          return this.type_to_action === 'CC'
        }),
        maxLength: maxLength(12),
        minLength: minLength(12),
      },
      machine_slip: {
        required: requiredIf(function() {
          return this.type_to_action === 'CC'
        }),
      },
      bank: {
        required: requiredIf(function() {
          return this.type_to_action === 'CC'
        }),
      },
      channel: {
        required: requiredIf(function() {
          return this.type_to_action === 'CC'
        }),
      },
      currency: {
        required: requiredIf(function() {
          return this.type_to_action === 'Cash' || this.type_to_action === 'Other'
        })
      },
      description: {
        required: requiredIf(function() {
          return this.type_to_action === 'Additional' || this.type_to_action === 'Other'
        })
      },
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_sales_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
    is_sales_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor')
    },
    is_operations_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('operations_supervisor')
    },
    gallery_images() {
      return this.payment_data.attachments.map(({file}) => file)
    }
  },
  created() {
    document.title = `Payment ID: ${this.$route.params.id} | Altezza Board`
    this.fetchItemsCash()
    this.fetchItemsCC()
    this.fetchItemsGear()
    this.fetchItemsAdditional()
    this.fetchItemsOther()
    getFinanceBanks({pos: true}).then(response => { this.bank_options = response.data })
    getCardTypes().then(response => { this.channel_options = response.data })
    getClients({ on_site_payment: this.$route.params.id }).then(response => {
      this.table_clients.items = response.data
    })
    getFinanceInvoiceList({ on_site_payment: this.$route.params.id }).then(response => {
      this.table_invoices.items = response.data
    })
    this.fetchData().then(() => {
      getClientsGroup(this.payment_data.client_group.id).then(response => {
        this.group_data = response.data
        getClientsLite({group: this.group_data.id})
          .then(response => { this.client_options = response.data })
        this.page_loaded = true
      })
    })
  },
  methods: {
    async fetchData() {
      const response = await getFinancePaymentsOnSite(this.$route.params.id)
      this.payment_data = response.data
    },
    fetchItemsCash() {
      getFinancePaymentsOnSiteCashList(this.$route.params.id)
        .then(response => {
          this.table_cash.items = response.data
        })
    },
    fetchItemsCC() {
      getFinancePosTransactionsList({ on_site_payment: this.$route.params.id })
        .then(response => {
          this.table_card.items = response.data
        })
    },
    fetchItemsGear() {
      getFinancePaymentsOnSiteGearList(this.$route.params.id)
        .then(response => {
          this.table_gear.items = response.data
        })
    },
    fetchItemsAdditional() {
      getFinancePaymentsOnSiteAdditionalList(this.$route.params.id)
        .then(response => {
          this.table_additional.items = response.data
        })
    },
    fetchItemsOther() {
      getFinancePaymentsOnSiteOtherList(this.$route.params.id)
        .then(response => {
          this.table_other.items = response.data
        })
    },
    sendAccount() {
      this.$refs.postAccount.preload = true
      patchFinancePaymentsOnSiteCashCashflow(this.$route.params.id, this.item_to_action.id)
        .then(() => {
          this.fetchItemsCash()
          this.$modal.hide('accountModal')
        })
        .catch(() => { this.$refs.postAccount.preload = false })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submitEdit.preload = true
      if (this.item_to_action) {
        if (this.type_to_action === 'CC') {
          patchFinancePosTransactions(this.item_to_action, { ...this.form, machine_slip: this.form.machine_slip.id })
            .then(() => {
              this.fetchItemsCC()
              this.$notify({
                type: 'success',
                title: 'Item saved',
              })
              this.onHideModal('editModal')
            })
            .catch(() => { this.$refs.submitEdit.preload = true })
        } else {
          action_map[this.type_to_action].patch(this.$route.params.id, this.item_to_action, this.form)
            .then(() => {
              this[`fetchItems${this.type_to_action}`]()
              this.$notify({
                type: 'success',
                title: 'Item saved',
              })
              this.onHideModal('editModal')
            })
            .catch(() => { this.$refs.submitEdit.preload = true })
        }
      } else {
        const data = {...this.form}
        if (this.type_to_action === 'CC') data.machine_slip = data.machine_slip.id
        action_map[this.type_to_action].post(this.$route.params.id, data)
          .then(() => {
            this[`fetchItems${this.type_to_action}`]()
            this.$notify({
              type: 'success',
              title: 'Item created',
            })
            this.onHideModal('editModal')
          })
          .catch(() => { this.$refs.submitEdit.preload = true })
      }
    },
    onShowAccountModal(row) {
      this.item_to_action = row
      this.$modal.show('accountModal')
    },
    onShowModal(row, type, modalType) {
      this.item_to_action = row?.id
      this.type_to_action = type
      this.form = {}
      if (modalType === 'editModal' && this.item_to_action) {
        this.form = {
          amount: row.amount,
        }
        if (this.type_to_action === 'Gear' || this.type_to_action === 'Additional') this.form.client = row.client.id
        if (this.type_to_action === 'Additional' || this.type_to_action === 'Other') this.form.description = row.description
        if (this.type_to_action === 'Cash' || this.type_to_action === 'Other') this.form.currency = row.currency.code
        if (this.type_to_action === 'CC') {
          this.form.rrn = row.rrn
          this.form.machine_slip = row.machine_slip
          this.form.bank = row.bank.id
          this.form.channel = row.channel.code
        }
      }
      this.$v.form.$reset()
      this.$modal.show(modalType)
    },
    onHideModal(modalType) {
      this.$modal.hide(modalType)
    },
    onDeleteItem() {
      this.$refs.deleteItem.preload = true
      action_map[this.type_to_action].delete(this.$route.params.id, this.item_to_action)
        .then(() => {
          this[`fetchItems${this.type_to_action}`]()
          this.$notify({
            type: 'success',
            title: 'Item deleted',
          })
          this.onHideModal('deleteModal')
        })
    },
    onShowActivitiesModal(data, modalType) {
      this.activities_modal_data = data[modalType]
      this.client_hash = data.hash
      this.activities_modal_type = modalType
      this.medical_clearance_present = !!data.medical_clearance
      this.$modal.show('activities-modal')
    },
    onHideModalActivities() {
      this.$modal.hide('activities-modal')
      this.medical_clearance_present = false
    },
    onUpdatePaymentData() {
      this.payment_completed_loader = true
      postFinancePaymentsOnSiteComplete(this.$route.params.id)
        .then(() => {
          this.fetchData()
          this.$notify({
            type: 'success',
            title: 'Change saved',
          })
          this.payment_completed_loader = false
        })
        .catch(() => { this.payment_completed_loader = false })
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    formatAmount(string) {
      return string ? Intl.NumberFormat('ru-RU').format(string) : '-'
    },
    tooltipFreeDaysContent(data) {
      return `${this.dateFormat(data.tour_start)} - ${this.dateFormat(data.tour_end)}<br>${data.location.short_name}`
    },
    tooltipIslandContent(data) {
      return `${data.name}<br>${this.dateFormat(data.tour_start)} - ${this.dateFormat(data.tour_end)}`
    },
    tooltipSnackBarContent(data) {
      if (!data.snack_bar) return
      if (!data.snack_bar.completed) return 'Not completed'
      if (data.snack_bar.no_need) return 'No need'
      if (data.snack_bar.completed && data.snack_bar.outstanding_amount > 0) return `Completed. Unpaid.<br>Items: ${data.snack_bar.items_count}<br>Order amount: ${data.snack_bar.order_amount.toLocaleString()}$<br>Credit: ${data.snack_bar.credit.toLocaleString()}$<br>Outstanding amount: ${data.snack_bar.outstanding_amount.toLocaleString()}$`
      if (data.snack_bar.completed && data.snack_bar.outstanding_amount === 0) return `Completed.<br>Items: ${data.snack_bar.items_count}<br>Order amount: ${data.snack_bar.order_amount.toLocaleString()}$<br>Credit: ${data.snack_bar.credit.toLocaleString()}$`
    },
    tooltipSafariContent(data) {
      return `${data.name}<br>${this.dateFormat(data.tour_start)} - ${this.dateFormat(data.tour_end)}`
    },
    tooltipTransportContent(data, transport_type) {
      if (!data[`${transport_type}_method`]) return
      return `${this.dateFormat(data[`${transport_type}_date`])} ${data[`${transport_type}_time`]}<br>${data[`${transport_type}_location`] ? data[`${transport_type}_location`].short_name : ''}<br>${data[`${transport_type}_flight_number`] || ''}`
    },
    tooltipDayTripsContent(data) {
      return `${data.ready_to_join ? '[J] ' : ''}${data.name}<br>${this.dateFormat(data.date)} ${data.pick_up_time}<br>${data.activity.short_name}`
    },
    tooltipInsuranceContent(data, type) {
      if (!data[`${type}_insurance_company`]) return
      return `${data[`${type}_insurance_company`]}<br>${data[`${type}_insurance_number`]}<br>${this.dateFormat(data[`${type}_insurance_coverage_start`])} - ${this.dateFormat(data[`${type}_insurance_coverage_end`])}<br>${data[`${type}_insurance_emergency_phone`]}`
    },
    tooltipClimbingContent(data) {
      return `${data.name}<br>${data.route.name} ${data.route.duration}<br>${this.dateFormat(data.tour_start)} - ${this.dateFormat(data.tour_end)}`
    },
    tooltipChecklistContent(data) {
      if (!data.completed || data.total_price <= 0) return
      return `Rental items: ${data.rental_items}<br>Purchase items: ${data.purchase_items}<br>Total price: ${data.total_price}`
    },
    onCopyTransportData(data, transport_type) {
      let string = `${this.dateFormat(data[`${transport_type}_date`])} ${data[`${transport_type}_time`]}\n${data[`${transport_type}_location`] ? data[`${transport_type}_location`].short_name : ''}`
      if (data[`${transport_type}_flight_number`]) string += `\n${data[`${transport_type}_flight_number`]}`
      this.copyToClipboard(string)
    },
    onCopyInsuranceData(data, type) {
      const string = `${data[`${type}_insurance_company`]}\n${data[`${type}_insurance_number`]}\n${this.dateFormat(data[`${type}_insurance_coverage_start`])} - ${this.dateFormat(data[`${type}_insurance_coverage_end`])}\n${data[`${type}_insurance_emergency_phone`]}`
      this.copyToClipboard(string)
    },
    copyToClipboard(string) {
      navigator.clipboard.writeText(string)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
  }
}
</script>

<style lang="sass" scoped>
.gallery
  display: grid
  grid-template-columns: repeat(auto-fill, 300px)
  gap: 10px
  margin-bottom: 30px

.gallery__preview
  position: relative
  aspect-ratio: 1
  border-radius: 10px
  overflow: hidden

  img
    width: 100%
    height: 100%
    object-fit: cover
</style>
